import { css } from '@compiled/react';

// Pre-click: gradient stays at the original 480px container height (responsive
// to viewport width via 100% width). Post-click: height scales dynamically with
// the measured inline preview content (see PaidJiraAiBackgroundV2 props).
// Breathing room (px) between the bottom of the results table and the
// gradient's bottom edge.
const RESULTS_BREATHING_ROOM_PX = 24;
// The container starts TOP_OVERHANG_PX above the prompt container,
// so total gradient height = overhang + (distance from prompt-top to preview-bottom) + breathing room.
const TOP_OVERHANG_PX = 100;
// Duration (ms) of the side decoration slide-out animation. Exported so callers
// can sequence other animations against it.
export const paidJiraAiBackgroundV2SlideMs = 600;

const containerStyle = css({
	zIndex: 0,
	position: 'absolute',
	insetBlockStart: -TOP_OVERHANG_PX,
	insetInlineStart: '50%',
	transform: 'translateX(-50%)',
	width: '100%',
	height: 480,
	pointerEvents: 'none',
	overflow: 'visible',
	// smoothly animate height change when results expand the background
	transition: 'height 600ms ease-in-out',
});

const gradientBandStyle = css({
	width: '100%',
	height: '100%',
	position: 'absolute',
	insetInlineStart: 0,
	insetBlockStart: 0,
	background:
		// eslint-disable-next-line local/ensure-wac-token-usage
		'conic-gradient(from 180deg at 50% 50%, #FCA700 1.7999999597668648deg, #82B536 70.19999742507935deg, #1868DB 128.85337471961975deg, #BF63F3 223.20000171661377deg, #F0F1F2 316.79999828338623deg, #FCA700 352.8000068664551deg)',
	opacity: 0.2,
	// Pixel offsets keep the slope angles constant regardless of container
	// height — top-left and bottom-right stay 86.4px from their edges, so the
	// band visually extends straight down as the container grows for results.
	clipPath: 'polygon(0 86.4px, 100% 0, 100% calc(100% - 86.4px), 0 100%)',
});

const leftVectorStyle = css({
	position: 'absolute',
	insetInlineStart: 0,
	insetBlockStart: 85,
	opacity: 1,
	transform: 'translateX(0)',
	transition: `transform ${paidJiraAiBackgroundV2SlideMs}ms ease-in-out, opacity ${paidJiraAiBackgroundV2SlideMs}ms ease-in-out`,
	'@media (max-width: 1023px)': {
		display: 'none',
	},
});

const leftVectorHiddenStyle = css({
	transform: 'translateX(-120%)',
	opacity: 0,
});

const leftTicketsStyle = css({
	position: 'absolute',
	insetInlineStart: 0,
	insetBlockStart: 149,
	backgroundImage: 'url("https://dam-cdn.atl.orangelogic.com/public/AT21HYS.webp")',
	backgroundSize: 'cover',
	backgroundRepeat: 'no-repeat',
	width: 273,
	height: 167.5,
	zIndex: 2,
});

const rightVectorStyle = css({
	position: 'absolute',
	insetInlineEnd: 0,
	insetBlockStart: -125,
	opacity: 1,
	transform: 'translateX(0)',
	transition: `transform ${paidJiraAiBackgroundV2SlideMs}ms ease-in-out, opacity ${paidJiraAiBackgroundV2SlideMs}ms ease-in-out`,
	'@media (max-width: 1023px)': {
		display: 'none',
	},
});

const rightVectorHiddenStyle = css({
	transform: 'translateX(120%)',
	opacity: 0,
});

const rightGoalsStyle = css({
	position: 'absolute',
	insetInlineEnd: 0,
	insetBlockStart: 124,
	backgroundImage: 'url("https://dam-cdn.atl.orangelogic.com/public/AT21HYR.webp")',
	backgroundSize: 'cover',
	backgroundRepeat: 'no-repeat',
	width: 285,
	height: 212.5,
	zIndex: 2,
});

interface PaidJiraAiBackgroundV2Props {
	/**
	 * When true, side decoration vectors slide off-screen and fade out.
	 */
	shouldSlideOut?: boolean;
	/**
	 * Measured height (px) of the inline preview / results section.
	 * When provided, the gradient grows to `prompt base + this + breathing room`.
	 * When undefined, stays at the fixed pre-click height.
	 */
	expandedHeight?: number;
}

export const PaidJiraAiBackgroundV2 = ({
	shouldSlideOut = false,
	expandedHeight,
}: PaidJiraAiBackgroundV2Props) => {
	// expandedHeight is the distance from the top of the prompt container down
	// to the bottom of the inline preview (measured by the parent). The gradient
	// container starts TOP_OVERHANG_PX above the prompt container, so total
	// height = overhang + that distance + small breathing room.
	// Add a 15% buffer past the measured content so the gradient extends just
	// beyond the bottom of the results table.
	const dynamicHeight =
		expandedHeight !== undefined
			? TOP_OVERHANG_PX + expandedHeight * 1.15 + RESULTS_BREATHING_ROOM_PX
			: undefined;
	return (
		<div
			css={containerStyle}
			style={dynamicHeight ? { height: dynamicHeight } : undefined}
			data-testid="paid-jira-ai-background"
		>
			<div css={gradientBandStyle} />
			<div css={[leftVectorStyle, shouldSlideOut && leftVectorHiddenStyle]}>
				<svg
					width="278"
					height="346"
					viewBox="0 0 278 346"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						d="M126.304 216.886C141.869 233.09 165.768 229.102 199.031 197.151C202.628 193.696 205.438 189.284 209.035 185.829L277.314 256.912C271.899 263.892 263.781 271.69 252.99 282.056C170.191 361.588 93.474 360.919 42.4374 307.786C4.43138 268.22 0.727808 230.217 3.22073 191.465C3.82247 163.224 5.10354 142.958 -8.71683 128.57C-24.2812 112.367 -48.1115 116.288 -81.4433 148.306L-93.0907 159.494L-160.514 89.3015C-154.209 81.4664 -146.09 73.6677 -136.19 64.1579C-52.5348 -16.1976 23.3597 -16.3841 75.219 37.6046C113.258 77.2054 116.038 116.03 114.333 154.025C113.8 182.2 112.45 202.531 126.27 216.919L126.304 216.886Z"
						fill="#FCA700"
					/>
				</svg>
				<div css={leftTicketsStyle} />
			</div>
			<div css={[rightVectorStyle, shouldSlideOut && rightVectorHiddenStyle]}>
				<svg
					width="259"
					height="422"
					viewBox="0 0 259 422"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						d="M387.189 211.854L214.396 384.134L111.034 351.343L69.1056 110.944L261.572 55.9512L387.189 211.854Z"
						fill="#BF63F3"
						stroke="#BF63F3"
						strokeWidth="6"
					/>
				</svg>
				<div css={rightGoalsStyle} />
			</div>
		</div>
	);
};
